import React, { useState, useEffect } from 'react';
import { Fuel, Plus, Loader2, Gauge, DollarSign, TrendingUp } from 'lucide-react';
import { gasExpenseAPI, userAPI } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export default function GasExpense() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    technician_id: user?.id || '',
    vehicle_name: '',
    gallons: 0,
    cost_per_gallon: 0,
    odometer: 0,
    miles_driven: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [exp, users] = await Promise.all([
        gasExpenseAPI.list(),
        userAPI.list().catch(() => []),
      ]);
      setExpenses(exp || []);
      setTechnicians(users || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await gasExpenseAPI.create({ ...formData, technician_id: formData.technician_id || user.id });
      toast.success('Gas expense added!');
      setDialogOpen(false);
      setFormData({ technician_id: user?.id || '', vehicle_name: '', gallons: 0, cost_per_gallon: 0, odometer: 0, miles_driven: 0 });
      loadData();
    } catch (error) {
      toast.error('Failed to add expense');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (a) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);
  const totalCost = expenses.reduce((s, e) => s + (e.total_cost || 0), 0);
  const totalGallons = expenses.reduce((s, e) => s + (e.gallons || 0), 0);
  const avgMpg = expenses.filter((e) => e.mpg).reduce((s, e, _, arr) => s + e.mpg / arr.length, 0);

  const getTechnicianName = (id) => {
    const t = technicians.find((t) => t.id === id);
    return t ? `${t.first_name} ${t.last_name}` : 'Unknown';
  };

  return (
    <div className="space-y-6 fade-in" data-testid="gas-expense-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2">Gas & Mileage Tracker</h1>
          <p className="text-muted-foreground">Track fuel costs and vehicle efficiency</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="btn-gradient-primary" data-testid="add-gas-button">
          <Plus className="w-4 h-4 mr-2" /> Log Fuel Up
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-orange border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground">Total Entries</p><p className="text-2xl font-bold">{expenses.length}</p></div>
              <Fuel className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-green border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground">Total Cost</p><p className="text-2xl font-bold">{formatCurrency(totalCost)}</p></div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-blue border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground">Total Gallons</p><p className="text-2xl font-bold">{totalGallons.toFixed(1)}</p></div>
              <Gauge className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="stat-purple border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div><p className="text-xs text-muted-foreground">Avg MPG</p><p className="text-2xl font-bold">{avgMpg.toFixed(1)}</p></div>
              <TrendingUp className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <Card><CardContent className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></CardContent></Card>
      ) : expenses.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Fuel className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No fuel entries yet</h3>
            <p className="text-muted-foreground mb-4">Start tracking your gas expenses</p>
            <Button onClick={() => setDialogOpen(true)} className="btn-gradient-primary">
              <Plus className="w-4 h-4 mr-2" /> Log First Entry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {expenses.map((exp) => (
            <Card key={exp.id} className="card-hover">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center">
                      <Fuel className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{exp.vehicle_name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {getTechnicianName(exp.technician_id)} • {new Date(exp.date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <div><p className="text-xs text-muted-foreground">Gallons</p><p className="font-semibold">{exp.gallons}</p></div>
                    <div><p className="text-xs text-muted-foreground">Per Gal</p><p className="font-semibold">{formatCurrency(exp.cost_per_gallon)}</p></div>
                    {exp.mpg && <div><p className="text-xs text-muted-foreground">MPG</p><p className="font-semibold">{exp.mpg.toFixed(1)}</p></div>}
                    <div><p className="text-xs text-muted-foreground">Total</p><p className="font-bold text-green-500">{formatCurrency(exp.total_cost)}</p></div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Log Fuel Up</DialogTitle>
            <DialogDescription>Track your gas expense</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Vehicle Name *</Label>
              <Input value={formData.vehicle_name} onChange={(e) => setFormData({ ...formData, vehicle_name: e.target.value })} placeholder="e.g., Service Van #1" required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Gallons *</Label>
                <Input type="number" step="0.01" min="0" value={formData.gallons} onChange={(e) => setFormData({ ...formData, gallons: parseFloat(e.target.value) || 0 })} required />
              </div>
              <div className="space-y-2">
                <Label>Cost per Gallon ($) *</Label>
                <Input type="number" step="0.001" min="0" value={formData.cost_per_gallon} onChange={(e) => setFormData({ ...formData, cost_per_gallon: parseFloat(e.target.value) || 0 })} required />
              </div>
              <div className="space-y-2">
                <Label>Odometer</Label>
                <Input type="number" step="0.1" value={formData.odometer} onChange={(e) => setFormData({ ...formData, odometer: parseFloat(e.target.value) || 0 })} />
              </div>
              <div className="space-y-2">
                <Label>Miles Driven</Label>
                <Input type="number" step="0.1" value={formData.miles_driven} onChange={(e) => setFormData({ ...formData, miles_driven: parseFloat(e.target.value) || 0 })} />
              </div>
            </div>
            <div className="p-3 bg-muted rounded-lg flex items-center justify-between">
              <span>Total Cost:</span>
              <span className="text-xl font-bold text-green-500">
                {formatCurrency((formData.gallons || 0) * (formData.cost_per_gallon || 0))}
              </span>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-gradient-primary" disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log Entry'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
